import React from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, PhoneMissedIcon, AlertCircleIcon, UserIcon, CheckCircleIcon } from 'lucide-react';
import { CallbackRecord } from '../types';
interface RecordCardProps {
  record: CallbackRecord;
  onAction: (id: string) => void;
  actionType: 'claim' | 'release';
}
const priorityConfig = {
  high: {
    color: 'bg-red-100 text-red-700 border-red-200',
    label: 'High Priority'
  },
  medium: {
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    label: 'Medium Priority'
  },
  low: {
    color: 'bg-teal-100 text-teal-700 border-teal-200',
    label: 'Low Priority'
  }
};
const typeConfig = {
  lead: {
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    label: 'Lead'
  },
  loan: {
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    label: 'Loan'
  }
};
export function RecordCard({
  record,
  onAction,
  actionType
}: RecordCardProps) {
  const {
    color,
    label
  } = priorityConfig[record.priority];
  const typeStyle = typeConfig[record.type];
  return <motion.div layout initial={{
    opacity: 0,
    y: 20,
    scale: 0.95
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.2
    }
  }} transition={{
    duration: 0.3,
    type: 'spring',
    bounce: 0.2
  }} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200" data-id="element-21">
      <div className="p-5 sm:p-6" data-id="element-22">
        <div className="flex justify-between items-start mb-4" data-id="element-23">
          <div className="flex items-center space-x-3" data-id="element-24">
            <div className="bg-slate-100 p-2 rounded-full" data-id="element-25">
              <UserIcon className="w-5 h-5 text-slate-600" data-id="element-26" />
            </div>
            <div data-id="element-27">
              <h3 className="text-lg font-semibold text-slate-900" data-id="element-28">
                {record.contactName}
              </h3>
              <div className="flex items-center text-slate-500 text-sm mt-0.5" data-id="element-29">
                <PhoneIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-30" />
                {record.phoneNumber}
              </div>
              {record.missedCalls > 0 && <div className="flex items-center text-red-600 text-xs font-medium mt-1" data-id="element-31">
                  <PhoneMissedIcon className="w-3.5 h-3.5 mr-1" data-id="element-32" />
                  {record.missedCalls} missed{' '}
                  {record.missedCalls === 1 ? 'call' : 'calls'}
                </div>}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2" data-id="element-33">
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${typeStyle.color}`} data-id="element-34">
              {typeStyle.label}
            </span>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${color}`} data-id="element-35">
              {label}
            </span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-2" data-id="element-36">
          <div className="flex flex-col space-y-1.5" data-id="element-37">
            {record.claimedAt && <div className="flex items-center text-sm text-indigo-600 font-medium" data-id="element-38">
                <CheckCircleIcon className="w-4 h-4 mr-1.5" data-id="element-39" />
                Claimed at {record.claimedAt}
              </div>}
          </div>

          <button onClick={() => onAction(record.id)} className={`
              relative overflow-hidden px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-offset-2
              ${actionType === 'claim' ? 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500 shadow-sm hover:shadow' : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:ring-slate-500'}
            `} data-id="element-40">
            {actionType === 'claim' ? 'Claim' : 'Release'}
          </button>
        </div>
      </div>
    </motion.div>;
}